/*
Copyright (c) 2025, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import React, { useEffect } from 'react';
import './styles.scss';
import { Tabs } from '@veneer/core';
import { withAnalytics } from '@veneer/analytics';
import { TabProps, TabItem } from './types';
import useThemeColor from '../../hooks/useThemeColor';
import { generateID } from '../../base/helper';

const TTab: React.FC<React.PropsWithChildren<TabProps>> = ({
  id = generateID('clientos-ui-toolkit-tab'),
  tabs,
  selectedTabId,
  onChange,
  className = ''
}) => {
  const contentColor = useThemeColor('foregroundDefault');
  const baseClass = 'clientos-ui-toolkit-tab';

  const enhancedTabs: TabItem[] = tabs.map((tab) => ({
    ...tab,
    id: generateID(`${id}__tab-${tab.id}`),
    label: tab.label,
    content: (
      <div
        style={{
          color: contentColor
        }}
        className={`${baseClass}__content`}
      >
        {tab.content}
      </div>
    ),
    'aria-disabled': tab.disabled
  }));

  const selectedTab = tabs.find((tab) => tab.id === selectedTabId);
  const customSelectedTabId = selectedTab?.disabled
    ? `${selectedTab.id}-disabled`
    : selectedTabId;

  useEffect(() => {
    const disabledTabs = document.querySelectorAll(
      '.vn-tab-list__tab[id*="-disabled"]'
    );

    disabledTabs.forEach((tab) => {
      const htmlTab = tab as HTMLElement;

      htmlTab.tabIndex = -1;

      const handleKeyDown = (event: Event) => {
        const e = event as KeyboardEvent;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      tab.addEventListener('keydown', handleKeyDown);

      return () => {
        tab.removeEventListener('keydown', handleKeyDown);
      };
    });
  }, []);

  return (
    <div
      className={baseClass}
      id={id}
    >
      <Tabs
        controlId={`${id}__tabs`}
        className={`${baseClass} ${className}`}
        tabs={enhancedTabs}
        selectedTabId={customSelectedTabId}
        onChangeTab={onChange}
        type="online"
      />
    </div>
  );
};

export const TabAnalytics = withAnalytics(TTab);
export default TTab;
